import { useQuery } from '@tanstack/react-query';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportTicket } from '../../models/Support/ISupportTicket';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS, type SupportTicketListParams } from './supportQueryHelpers';
import { buildSupportTicketFilterRequest } from './useSupportTicketsQuery';

const ACCOUNT_SUPPORT_TICKETS_PAGE_SIZE = 25;

/**
 * Open (non-closed, non-spam, non-archive) tickets for one account. `useSupportTicketsQuery` is the inbox's
 * mailbox-scoped list and disables itself without a mailbox id; the account surfaces have no mailbox
 * to scope to, so this passes an empty mailbox id to drop that criterion. The filter endpoint takes
 * no mailbox parameter and narrows to the caller's agent mailboxes server-side, so the result already
 * spans every mailbox the viewer can see. Viewers who agent no mailbox get a 403, which reads as an
 * empty list rather than an error toast.
 */
export function useAccountSupportTicketsQuery(accountId: string, options: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	const filterRequest = buildSupportTicketFilterRequest({ filter: 'all', mailboxId: '', accountId });
	const queryParams: SupportTicketListParams = {
		filterRequest,
		pageSize: ACCOUNT_SUPPORT_TICKETS_PAGE_SIZE,
	};

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.list(queryParams),
		staleTime: 30_000,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden(
				() =>
					userSession.webServiceHelper.callAsync<IPagedCollection<ISupportTicket>>({
						path: SUPPORT_PATHS.tickets.list(queryParams),
						method: 'POST',
						body: filterRequest,
					}),
				{ values: [], totalCount: 0 }
			),
		enabled: options.enabled !== false && !!accountId,
		meta: { toastErrorMessage: 'Failed to load support tickets' },
	});
}
